import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/components/ui/card";
import { cn } from "@/ui/lib/utils";
import type { TaxonomyOtherLabel } from "@/ml-exports/types/taxonomy";

/* ──────────────────────────────────────────────────────────────────
 * TaxonomyOtherLabels — labels outside the taxonomy families/groups.
 * Grouped by `category`, styled by `lifecycle` (deprecated is dimmed
 * and struck through). Renders nothing when the API returns none.
 * ────────────────────────────────────────────────────────────────── */

const groupByCategory = (labels: TaxonomyOtherLabel[]) => {
  const byCategory = new Map<string, TaxonomyOtherLabel[]>();
  for (const label of labels) {
    const category = label.category || "unknown";
    const bucket = byCategory.get(category);
    if (bucket) bucket.push(label);
    else byCategory.set(category, [label]);
  }
  return [...byCategory.entries()];
};

interface TaxonomyOtherLabelsProps {
  labels?: TaxonomyOtherLabel[] | null;
}

export const TaxonomyOtherLabels = ({ labels }: TaxonomyOtherLabelsProps) => {
  const { t } = useTranslation("settings");
  const categories = groupByCategory(labels ?? []);
  if (categories.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-muted-foreground" />
          {t("otherLabels.title")}
        </CardTitle>
        <CardDescription>{t("otherLabels.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {categories.map(([category, categoryLabels]) => (
          <div key={category}>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 font-semibold">
              {t("otherLabels.categoryHeading", {
                category,
                count: categoryLabels.length,
              })}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {categoryLabels.map((label) => (
                <span
                  key={label.canonical}
                  title={label.canonical}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs",
                    category === "legacy"
                      ? "border-dashed border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400"
                      : "bg-muted text-muted-foreground",
                    label.lifecycle === "deprecated" &&
                      "line-through opacity-50",
                  )}
                >
                  {label.display_name}
                  <span className="font-mono text-[10px] opacity-70">
                    {label.lifecycle}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default TaxonomyOtherLabels;
